import "react-phone-number-input/style.css";
import { Button, Col, Row, Typography } from "antd";
import {
  ContentBlock,
  ProductScenario,
  Product as ProductType,
  ScenarioItem,
  Scenario as ScenarioType,
} from "api/dialog";
import {
  bitrixLeadFromDeal,
  frontendAjaxCall,
  getAuthedUser,
  getProductInfo,
  productLink,
} from "api/hooks";
import { ContentBox } from "components/ContentBox";
import { Paginator } from "components/Paginator";
import { Scenario, ScenarioProps } from "components/Scenario";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  getWidgetState,
  resetWidgetState,
  setWidgetState,
  setWidgetStateField,
  usePersistentState,
} from "utils/useStorage";
import {
  appendParams,
  appendParamsToPathname,
  cssGrayscale,
  getCartItemFromCart,
  promisedComponent,
  replaceWindowUrl,
  useMobileQuery,
} from "utils/utils";
import { useState } from "react";
import LeftArrow from "resources/LeftArrow.svg";
import { animated } from "@react-spring/web";
import { ChangeValue } from "components/DialogBox";
import { evalAccessStr } from "utils/evaluator";
import { Context } from "components/Scenario/utils";
import { rangeFromZero } from "utils/generators";
import { useScrollableList } from "utils/useScrollableList";
import { Loader } from "components/Loader";
import { usePromise, usePromiseExt } from "utils/usePromise";
import { emptyErr, ok } from "utils/types";
import { FRONTEND_DOMAIN } from "config";
import _styles from "./Product.module.scss";
import classNames from "classnames/bind";
import { useLanguage } from "utils/useLanguage";
import { useEffectOnce, useMount } from "react-use";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
const styles = classNames.bind(_styles);

const getLatestContentBlock = (
  scenario: ScenarioType,
  branchesStack: [branch: string, state: number, context: Context][]
): [string, number] | undefined => {
  const reversedStack = [...branchesStack].reverse();

  for (const [branch, state] of reversedStack)
    for (const block of scenario[branch].slice(0, state).reverse())
      if (block.contentBlock) return [branch, scenario[branch].indexOf(block)];
};

export type TimeoutState =
  | "beforePayment"
  | "beforeSign"
  | "afterSignToRepresentativeOfficeStarted";

const getLatestTimeout = (
  scenario: ScenarioType,
  branchesStack: [branch: string, state: number, context: Context][]
): TimeoutState | undefined => {
  const reversedStack = [...branchesStack].reverse();
  for (const [branch, state, context] of reversedStack)
    for (const block of scenario[branch].slice(0, state).reverse()) {
      if (block.dialogBlock.type === "calculatePrice") return "beforeSign";
      if (block.dialogBlock.type === "signContract") return;
      if (block.dialogBlock.type === "signUpToRepresentativeOffice")
        return "afterSignToRepresentativeOfficeStarted";
      if (context.state === "signing") return "beforeSign";
    }
  return "beforePayment";
};

const getContentBlockIndicies = (
  scenario: ScenarioType,
  branch = "initial",
  processed: string[] = []
): { branch: string; index: number }[] => {
  if (processed.includes(branch)) return [];
  processed.push(branch);
  const scenarioBranch = scenario[branch];
  if (!scenarioBranch) return [];
  return scenarioBranch.flatMap((block, index) => {
    const content = block.contentBlock ? [{ branch, index }] : [];
    const branchContent = [];

    if (
      block.dialogBlock?.blockType === "interactive" &&
      block.dialogBlock.type === "branch"
    ) {
      branchContent.push(
        ...block.dialogBlock.matchers.flatMap(({ next: _branch }) =>
          getContentBlockIndicies(scenario, _branch, processed)
        )
      );
    }
    if (
      block.dialogBlock?.blockType === "interactive" &&
      "branching" in block.dialogBlock &&
      block.dialogBlock.branching
    ) {
      branchContent.push(
        ...[
          block.dialogBlock.branching.default,
          ...(typeof block.dialogBlock.branching.next === "string"
            ? block.dialogBlock.branching.next
            : (block.dialogBlock.branching.next ?? []).map(({ branch }) => branch)),
        ].flatMap((_branch) => getContentBlockIndicies(scenario, _branch, processed))
      );
    }
    return [...content, ...branchContent];
  });
};

export const setPath = <T extends Record<string, unknown>>(
  target: T,
  path: (string | number)[],
  value: unknown
): typeof target => {
  if (path.length === 0)
    //@ts-ignore
    return typeof target === "object" &&
      typeof value === "object" &&
      !(value instanceof Date) &&
      !Array.isArray(target)
      ? { ...target, ...value }
      : value;
  const [head, ...rest] = path;
  const keys = Array.isArray(target)
    ? head === "_"
      ? rangeFromZero(target.length)
      : [Number(head)]
    : [head];
  const copy = target;

  for (const key of keys) {
    //@ts-ignore
    copy[key] = setPath(
      (target[key] ?? (Number.isInteger(Number(rest[0])) ? [] : {})) as T,
      rest,
      value
    );
  }

  return copy as unknown as T;
};

export const parsePath = (
  path: string,
  ctx: Record<string, unknown> = {}
): (string | number)[] => {
  return evalAccessStr(path, ctx);
};

const oneClickOrder = (): ScenarioItem[] => [
  {
    dialogBlock: {
      blockType: "content",
      type: "text",
      text: i18next.t("widget.oneClickOrder.greetingText"),
    },
  },
  {
    dialogBlock: {
      blockType: "interactive",
      type: "form",
      allowDiia: false,
      inputs: [
        {
          blockName: "name",
          type: "input",
          required: true,
          title: i18next.t("widget.oneClickOrder.nameTitle"),
          placeholder: i18next.t("widget.oneClickOrder.namePlaceholder"),
        },
        {
          blockName: "phone",
          type: "phone",
          required: true,
          title: i18next.t("widget.oneClickOrder.phoneTitle"),
          placeholder: i18next.t("widget.oneClickOrder.phonePlaceholder"),
        },
      ],
      blockName: "ctx.contact",
    },
  },
  {
    dialogBlock: {
      blockType: "interactive",
      type: "bitrixTrigger",
      TITLE: "Покупка в один клик",
      CATEGORY_ID: "0",
      TYPE_ID: "SALE",
    },
  },
  {
    dialogBlock: {
      blockType: "content",
      type: "text",
      text: i18next.t("widget.oneClickOrder.contactYouLater"),
    },
  },
];

export function Product({
  disableTimeout,
  initialState,
  product: {
    scenario = { initial: [] },
    background,
    title,
    settings: {
      bitrixConfig,
      paymentTimeout,
      signTimeout,
      signUpToRepresentativeOfficeTimeout,
    } = {},
  },
  showFaq = true,
  mobile: isMobile = false,
  onChange,
}: {
  disableTimeout?: boolean;
  product: ProductScenario;
  mobile?: boolean;
  showFaq?: boolean;
  onChange?: (value: ChangeValue<unknown> | null, context: Context) => void;
} & Pick<ScenarioProps, "initialState">) {
  scenario = { oneClickOrder: oneClickOrder(), ...scenario };
  const contentBlockIndicies: { branch: string; index: number }[] =
    getContentBlockIndicies(scenario);
  const [contentBlockIndex, setContentBlockIndex] = usePersistentState<number>(
    "contentBlockIndex",
    0
  );
  const getBackground = () => {
    if (!contentBlockIndicies[contentBlockIndex]) return background;
    const { branch, index } = contentBlockIndicies[contentBlockIndex];
    return scenario[branch][index].contentBlock?.background ?? background;
  };
  const getTextColor = () => {
    if (isMobile) return 0;
    if (!contentBlockIndicies[contentBlockIndex]) return 255;
    const { branch, index } = contentBlockIndicies[contentBlockIndex];
    const grayscale = scenario[branch][index].contentBlock?.textColor ?? 255;
    return grayscale;
  };
  const [timeoutState, setTimeoutState] = useState<TimeoutState | undefined>(
    "beforePayment"
  );
  const scenarioComponent = (
    <Scenario
      title={
        isMobile && (
          <Typography.Text className={styles("title")} style={{ marginBottom: "auto" }}>
            {title}
          </Typography.Text>
        )
      }
      initialState={initialState}
      scenario={scenario}
      contrastGrayscale={getTextColor()}
      timeout={(() => {
        if (disableTimeout || !timeoutState) return;
        const item = {
          beforePayment: paymentTimeout,
          beforeSign: signTimeout,
          afterSignToRepresentativeOfficeStarted: signUpToRepresentativeOfficeTimeout,
        }[timeoutState];
        if (!item) return;
        return {
          delay: item.delay,
          callback: (context) => {
            if (!item) return;
            const contact = {
              ...(context.contact as any),
              name: (context.contact as any)?.name ?? "Anonymous",
            };

            bitrixLeadFromDeal(item.deal)(contact);
          },
        };
      })()}
      bitrixConfig={bitrixConfig}
      onStackChange={(stack) => {
        const latestTimeout = getLatestTimeout(scenario, stack);
        setTimeoutState(latestTimeout);

        const latestContentBlock = getLatestContentBlock(scenario, stack);
        if (!latestContentBlock) return;

        const [branch2, index2] = latestContentBlock;
        setContentBlockIndex(
          contentBlockIndicies.findIndex(
            ({ branch, index }) => branch === branch2 && index === index2
          )
        );
      }}
      onChange={onChange}
    />
  );
  const paginatorComponent = (
    <Paginator
      showFaq={showFaq}
      items={contentBlockIndicies}
      page={contentBlockIndex}
      onPageChange={setContentBlockIndex}
      render={({ item: { branch, index } }) => {
        return (
          <ContentBox
            style={{ marginTop: "auto" }}
            content={scenario[branch][index].contentBlock as ContentBlock}
          />
        );
      }}
    />
  );

  const pages = [paginatorComponent, scenarioComponent];
  const [page, setPage] = useState(1);
  const [component, props] = useScrollableList<HTMLDivElement>(pages.length, page);

  const backgroundImage = getBackground();

  useMount(() => {
    window.document.body.scrollIntoView({ block: "end" });
    setTimeout(() => window.document.body.scrollIntoView({ block: "end" }), 500);
  });

  return (
    <div
      style={
        !isMobile
          ? { backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none" }
          : {}
      }
      className={styles("container")}
      ref={component}
    >
      {!isMobile ? (
        <Row
          className={styles("row")}
          wrap={false}
          gutter={[24, 0]}
          justify="space-between"
        >
          <Col className={styles("left")}>
            <Typography.Text
              className={styles("title")}
              style={{ color: cssGrayscale(getTextColor()) }}
            >
              {title}
            </Typography.Text>
            <div style={{ flex: 1 }}>{paginatorComponent}</div>
          </Col>
          <Col style={{ flexBasis: 560 + 24 }} className={styles("right")}>
            {scenarioComponent}
          </Col>
        </Row>
      ) : (
        props.map((style, i) => {
          return (
            // <animated.div className={styles('page')} {...bind()} key={i} style={style}>
            <animated.div
              className={styles("page")}
              key={i}
              style={{ ...style, padding: i === 1 ? 0 : undefined }}
            >
              <Button
                onClick={() => setPage((page + 1) % 2)}
                style={{
                  padding: "12px",
                  borderRadius: i !== 0 ? "0px 16px 16px 0px" : "16px 0px 0px 16px",
                  position: "absolute",
                  [i !== 0 ? "left" : "right"]: "0",
                  top: "56px",
                  width: "fit-content",
                  height: "fit-content",
                  zIndex: 3,
                }}
                icon={
                  i === 0 ? (
                    <>
                      <img src={LeftArrow} alt="Arrow" />
                      <img src={LeftArrow} alt="Arrow" />
                    </>
                  ) : (
                    <>
                      <img
                        src={LeftArrow}
                        alt="Arrow"
                        style={{ transform: "rotate(180deg)" }}
                      />
                      <img
                        src={LeftArrow}
                        alt="Arrow"
                        style={{ transform: "rotate(180deg)" }}
                      />
                    </>
                  )
                }
              />
              {pages[i]}
            </animated.div>
          );
        })
      )}
    </div>
  );
}

export const ProductBody = ({
  product,
  initialContext,
  disableTimeout,
  showFaq,
  onNextProduct,
}: {
  product: ProductScenario;
  disableTimeout?: boolean;
  onNextProduct?: () => void;
  showFaq?: boolean;
  initialContext: any;
}): JSX.Element => {
  const isMobile = useMobileQuery();

  useEffectOnce(() => {
    // const searchParams = new URLSearchParams(_window.location.search ?? "");
    const onCartDelete = (async (e: CustomEvent<{ contractId: string }>) => {
      // const { contractId } = e.detail;
      // const cartItemId = searchParams.get("cartItemId");
      // console.log("cart contract destroy", e);
      // if (contractId === cartItemId) {
      //   const {
      //     data: { contracts: cartItems },
      //   } = await frontendAjaxCall("/web/cart", { method: "GET" });
      //   if (cartItems.length === 0) {
      //     searchParams.delete("cartItemId");
      //   } else {
      //     searchParams.set("cartItemId", cartItems[0].id);
      //   }
      //   resetWidgetState();
      //   window.location.search = searchParams.toString();
      //   // TODO: maybe just refetch and rerender? although may look janky just suddenly changing everything
      //   window.location.reload();
      // }
    }) as unknown as EventListener;

    window.top?.addEventListener("cart-contract-destroy", onCartDelete);
    return () => {
      window.top?.removeEventListener("cart-contract-destroy", onCartDelete);
    };
  });

  return (
    <Product
      showFaq={showFaq}
      disableTimeout={disableTimeout}
      initialState={{
        context: initialContext,
      }}
      mobile={isMobile}
      product={product}
      onChange={(value) => {
        if (value?.resetProduct) onNextProduct?.();
      }}
    />
  );
};

export const ProductPage = (): JSX.Element => {
  const { i18n } = useTranslation();
  const { productId: productIdString } = useParams<"productId">();
  const [searchParams] = useSearchParams();
  const topSearchParams = new URLSearchParams(window.top?.location.search ?? "");
  const productId = Number(productIdString ?? searchParams.get("productId") ?? "8");
  const lang = useLanguage();
  const scenario: "insurance" | "incident" =
    (searchParams.get("scenario") as "insurance" | "incident") ?? "insurance";
  const forceState = searchParams.get("state") ?? topSearchParams.get("state");
  const cartItemId = searchParams.get("cartItemId") ?? topSearchParams.get("cartItemId");
  const policyNo = searchParams.get("policyNo") ?? topSearchParams.get("policyNo");
  const state = getWidgetState();
  const { state: promised } = usePromiseExt(
    async () => {
      // get local storage state's cartItemId
      const stateCartItem = state?.context?.cartItemId;

      // if was given an cartItemId in parameters - recover its state from backend
      if (cartItemId) {
        try {
          const {
            data: { contracts: cartItems },
          } = await frontendAjaxCall("/web/cart", {
            method: "GET",
          });
          const cartItem = getCartItemFromCart(cartItems, cartItemId);
          const cartItemProductId = cartItem.widget_state.context.productId;

          // if cart item with specified id does not exists, then remove cartItemId from params
          if (!cartItem) {
            topSearchParams.delete("cartItemId");
            searchParams.delete("cartItemId");
            const topUrl = appendParams(window.top, topSearchParams);
            const rootUrl = appendParams(window, searchParams);
            replaceWindowUrl(window.top, topUrl);
            replaceWindowUrl(window, rootUrl);

            // if cart item with specified id does exists, but does not match current product - redirect to the correct product
          } else if (cartItemProductId !== productId) {
            const productInfo = await getProductInfo(cartItemProductId, lang);
            const origin = window.top?.location.origin;
            const _productLink = productLink(productInfo.slug, lang, origin);
            const url = appendParamsToPathname(_productLink, topSearchParams);
            window.top?.location.assign(url);

            // otherwise just replace local storage state with cartItem's state
          } else {
            setWidgetState(cartItem.widget_state);
          }
        } catch {}
        // else if none was given, but current state belongs to some cartItem - reset state to start fresh scenario
      } else if (stateCartItem) {
        resetWidgetState();
      }

      // if was given "state" parameter - make needed changes to state
      if (forceState === "payment") {
        setWidgetStateField("branch", "payment");
        setWidgetStateField("scenarioState", 0);
      } else if (forceState === "change") {
        setWidgetStateField("branch", "change");
        setWidgetStateField("scenarioState", 0);
      } else if (forceState === "sign" && policyNo) {
        try {
          await getAuthedUser();

          setWidgetStateField("branch", "sign");
          setWidgetStateField("scenarioState", 0);
          setWidgetStateField("context", {
            ...getWidgetState().context,
            policyNo,
            authed: undefined,
            productId,
          });
        } catch {
          resetWidgetState();
        }
      } else {
        if (state?.context?.productId !== productId) resetWidgetState();
      }

      // if state user and current user does not match - reset
      if (state?.context?.authed?.id) {
        try {
          const { data: user } = await getAuthedUser();
          if (state.context.authed.id !== user.id) resetWidgetState();
        } catch {
          resetWidgetState();
        }
      }

      const productInfo = await getProductInfo(productId, lang);

      if (productInfo.translation)
        i18n.addResourceBundle(lang, "translation", productInfo.translation);

      return ok(productInfo);
    },
    [],
    { doInitialFetch: true }
  );

  return promisedComponent<(typeof promised & { state: "resolved" })["res"]>(promised, {
    loading: Loader({ mode: "default" }),
    resolved: ({ res: product }) => {
      if (window.top === window) {
        // hack so that we redirect to prev page after login
        window.location.replace(FRONTEND_DOMAIN + "/product/" + product.slug);
        return <></>;
      }

      return (
        <>
          <ProductBody
            showFaq={false}
            initialContext={{
              relevant: product.relevant,
              productId,
              slug: product.slug,
              productName: product.title,
            }}
            product={{
              title: product.title,
              background: product.background,
              ...product[scenario],
            }}
            // lang={lang}
          />
          <div ref={(ref) => ref?.scrollIntoView(false)} />
        </>
      );
    },
  });
};

export const CartPage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang") ?? "ru";
  const promised = usePromise(async () => {
    try {
      const { data: cartItems } = await frontendAjaxCall("/web/cart", {
        method: "GET",
      });
      if (Array.isArray(cartItems)) {
        window.top?.location.assign(FRONTEND_DOMAIN);
        return emptyErr<{
          product: ProductType & {
            slug: string;
          };
          productId: any;
        }>();
      }
      const cartItemId =
        Object.entries<any>(cartItems).find(
          ([
            ,
            {
              attributes: { widget_state },
            },
          ]) => !(widget_state?.context && "ppDocument" in widget_state.context)
        )?.[0] ?? Object.keys(cartItems)[0];
      const cartItem = cartItems[cartItemId];
      const productId = cartItem.attributes.id_product;
      const productInfo = await getProductInfo(productId, lang);
      setWidgetState(cartItem.attributes.widget_state);
      const result = ok({ product: productInfo, productId });
      return result;
    } catch {
      window.top?.location.assign(FRONTEND_DOMAIN);
      return emptyErr<{
        product: ProductType & {
          slug: string;
        };
        productId: any;
      }>();
    }
  });

  return promisedComponent<(typeof promised & { state: "resolved" })["res"]>(promised, {
    loading: Loader({ mode: "default" }),
    resolved: ({ res: { product, productId } }) => {
      return (
        <>
          <ProductBody
            showFaq={false}
            productId={productId}
            product={{
              title: product.title,
              background: product.background,
              ...product.insurance,
            }}
            relevant={product.relevant}
          />
          <div ref={(ref) => ref?.scrollIntoView(false)} />
        </>
      );
    },
  });
};
